import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { RequestError } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { useToast } from '../../components/Toast.js';

/**
 * The pieces every admin section needs and none of them should re-invent: a
 * card, a labelled number, a row, and one way of running a write.
 */

export function AdminCard({
  title,
  hint,
  action,
  children,
}: {
  title?: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="glass-card rounded-xl p-4 space-y-3">
      {(title || action) && (
        <div className="flex items-center gap-2">
          {title && <h2 className="text-sm font-semibold flex-1 truncate">{title}</h2>}
          {action}
        </div>
      )}
      {hint && <p className="text-xs text-muted-foreground leading-relaxed">{hint}</p>}
      {children}
    </section>
  );
}

const BARS = {
  primary: 'bg-primary',
  good: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-destructive',
  info: 'bg-info',
  muted: 'bg-muted-foreground/40',
} as const;

/** A number with its name under it. The accent bar is what tells them apart. */
export function Stat({
  label,
  value,
  tone = 'primary',
}: {
  label: string;
  value: ReactNode;
  tone?: keyof typeof BARS;
}) {
  return (
    <div className="glass-card rounded-xl px-3 py-2.5 flex items-center gap-2.5">
      <span className={cn('w-1 self-stretch rounded-full shrink-0', BARS[tone])} />
      <div className="min-w-0">
        <p className="text-lg font-semibold ltr-nums leading-tight">{value ?? '—'}</p>
        <p className="text-[11px] text-muted-foreground truncate">{label}</p>
      </div>
    </div>
  );
}

/** One entry in a list: an icon, a title, a subtitle, and whatever acts on it. */
export function Row({
  icon,
  title,
  subtitle,
  meta,
  actions,
  onClick,
}: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
}) {
  const interactive = onClick !== undefined;
  return (
    <div
      {...(interactive ? { role: 'button', tabIndex: 0, onClick } : {})}
      onKeyDown={interactive ? (event) => event.key === 'Enter' && onClick?.() : undefined}
      className={cn(
        'glass-card rounded-lg px-3 py-2.5 flex items-center gap-3',
        interactive && 'cursor-pointer hover:border-primary/20 transition-colors',
      )}
    >
      {icon && <span className="shrink-0 text-muted-foreground">{icon}</span>}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
      </div>
      {meta && <div className="shrink-0 text-[11px] text-muted-foreground ltr-nums text-end">{meta}</div>}
      {actions && <div className="shrink-0 flex items-center gap-1.5">{actions}</div>}
    </div>
  );
}

export function Pill({ tone = 'neutral', children }: { tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  const tones = {
    neutral: 'bg-muted text-muted-foreground',
    good: 'bg-success/10 text-success',
    warn: 'bg-warning/10 text-warning',
    bad: 'bg-destructive/10 text-destructive',
    info: 'bg-primary/10 text-primary',
  } as const;
  return <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0', tones[tone])}>{children}</span>;
}

/**
 * Every write in the back office fails for a reason the server named — the last
 * account in a business, your own row, staff the environment file holds — so the
 * code it answers with is the message. A generic "something went wrong" would
 * hide which rule was hit, and these are rules a person can do something about.
 */
export function useAdminAction(invalidate: string[][] = []) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const run = async (
    work: () => Promise<unknown>,
    successKey?: string,
    // Some confirmations name what they acted on ("an invitation was sent to
    // …"), which the key alone cannot say.
    params?: Record<string, unknown>,
  ) => {
    setBusy(true);
    try {
      await work();
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
      if (successKey) toast.success(t(successKey, params));
      return true;
    } catch (error) {
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
}
